import React, { useState, useMemo } from 'react';
import {
  BookOpen,
  Music,
  FileAudio,
  Gamepad2,
  Bookmark,
  Sparkles,
  Plus,
  Filter,
  Edit2,
  Trash2,
  Eye,
  Copy,
  Archive,
  Layers,
  CheckCircle2,
  Play,
  Pause,
  X,
  RotateCcw,
  Clock,
  ArrowRight,
  ShieldCheck,
  Check,
  Award,
} from 'lucide-react';
import {
  SutraCatalogItem,
  GathaSubmission,
  StavanItem,
  AudioAsset,
  QuizGameItem,
  AcademicLevel,
  AdminRole,
} from '../types';
import { Badge, Button, Input, Search, Tooltip } from '../../components/core';

export type ContentCategory = 'Sutras' | 'Stavans' | 'Audio Library' | 'Games' | 'Stories' | 'Future Categories';

export interface ContentItem {
  id: string;
  name: string;
  category: ContentCategory;
  assignedLevel: string;
  type: string;
  status: 'Active' | 'Archived' | 'Draft';
  lastUpdated: string;
  rawItem?: any;
}

interface UnifiedLearningContentProps {
  sutras: SutraCatalogItem[];
  submissions: GathaSubmission[];
  stavans: StavanItem[];
  audioAssets: AudioAsset[];
  games: QuizGameItem[];
  levels: AcademicLevel[];
  currentRole: AdminRole;
  onAddSutra: (sutra: SutraCatalogItem) => void;
  onUpdateSutra: (sutra: SutraCatalogItem) => void;
  onApproveSubmission: (sub: GathaSubmission, points: number, notes: string) => void;
  onRejectSubmission: (sub: GathaSubmission, notes: string) => void;
  onAddStavan: (stavan: StavanItem) => void;
  onUpdateStavan: (stavan: StavanItem) => void;
  onDeleteStavan: (id: string) => void;
  onAddAudio: (asset: AudioAsset) => void;
  onUpdateAudio: (asset: AudioAsset) => void;
  onDeleteAudio: (id: string) => void;
  onAddGame: (game: QuizGameItem) => void;
  onUpdateGame: (game: QuizGameItem) => void;
  onDeleteGame: (id: string) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const UnifiedLearningContent: React.FC<UnifiedLearningContentProps> = ({
  sutras,
  submissions,
  stavans,
  audioAssets,
  games,
  levels,
  currentRole,
  onAddSutra,
  onUpdateSutra,
  onApproveSubmission,
  onRejectSubmission,
  onAddStavan,
  onUpdateStavan,
  onDeleteStavan,
  onAddAudio,
  onUpdateAudio,
  onDeleteAudio,
  onAddGame,
  onUpdateGame,
  onDeleteGame,
  addToast,
}) => {
  const isAuditor = currentRole === 'Auditor';

  // Active Category State
  const [activeCategory, setActiveCategory] = useState<ContentCategory>('Sutras');
  const [viewSubmissionsQueue, setViewSubmissionsQueue] = useState(false);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Audio Playback
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);

  // Modals
  const [viewingItem, setViewingItem] = useState<ContentItem | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [levelAssignItem, setLevelAssignItem] = useState<ContentItem | null>(null);
  const [assignLevelSelect, setAssignLevelSelect] = useState<string>('Level 1');

  // Submission review modal
  const [selectedSubmission, setSelectedSubmission] = useState<GathaSubmission | null>(null);
  const [reviewPoints, setReviewPoints] = useState(25);
  const [reviewNotes, setReviewNotes] = useState('');

  // Story & Future mock local state for full editing
  const [customStories, setCustomStories] = useState<ContentItem[]>([
    {
      id: 'STORY-1',
      name: 'Story of King Kumarapala & Hemachandracharya',
      category: 'Stories',
      assignedLevel: 'Level 2 - Madhyamik',
      type: 'Moral Story',
      status: 'Active',
      lastUpdated: '2026-07-20',
      rawItem: { description: 'Historical narrative on non-violence (Ahimsa) and Jain principles.' },
    },
    {
      id: 'STORY-2',
      name: 'Chandanbala and Bhagwan Mahavira',
      category: 'Stories',
      assignedLevel: 'Level 1 - Prarambhik',
      type: 'Devotional Story',
      status: 'Active',
      lastUpdated: '2026-07-18',
      rawItem: { description: 'Inspirational story of pure devotion and charity (Anekantavada).' },
    },
  ]);

  const [customFuture, setCustomFuture] = useState<ContentItem[]>([
    {
      id: 'FUT-1',
      name: 'Interactive Jain Geography 3D Map',
      category: 'Future Categories',
      assignedLevel: 'Level 3 - Shravak Junior',
      type: 'Interactive Map',
      status: 'Draft',
      lastUpdated: '2026-07-22',
      rawItem: { description: 'Upcoming 3D visualization of Jambudvipa and Sacred Tirthas.' },
    },
  ]);

  // Unified items mapping
  const unifiedItems: ContentItem[] = useMemo(() => {
    const list: ContentItem[] = [];

    // Sutras
    sutras.forEach((s) => {
      list.push({
        id: s.id,
        name: s.name,
        category: 'Sutras',
        assignedLevel: `Level ${s.targetLevel}`,
        type: 'Sutra Recitation',
        status: 'Active',
        lastUpdated: '2026-07-21',
        rawItem: s,
      });
    });

    // Stavans
    stavans.forEach((st) => {
      list.push({
        id: st.id,
        name: st.title,
        category: 'Stavans',
        assignedLevel: 'All Levels',
        type: `Bhagwan ${st.dedicatedBhagwan}`,
        status: 'Active',
        lastUpdated: '2026-07-19',
        rawItem: st,
      });
    });

    // Audio Library
    audioAssets.forEach((a) => {
      list.push({
        id: a.id,
        name: a.title,
        category: 'Audio Library',
        assignedLevel: `Level ${a.levelTarget}`,
        type: a.category,
        status: 'Active',
        lastUpdated: '2026-07-22',
        rawItem: a,
      });
    });

    // Games
    games.forEach((g) => {
      list.push({
        id: g.id,
        name: g.title,
        category: 'Games',
        assignedLevel: `Level ${g.levelTarget}`,
        type: `${g.category} (${g.difficulty})`,
        status: 'Active',
        lastUpdated: '2026-07-23',
        rawItem: g,
      });
    });

    // Stories
    customStories.forEach((st) => list.push(st));

    // Future
    customFuture.forEach((f) => list.push(f));

    return list;
  }, [sutras, stavans, audioAssets, games, customStories, customFuture]);

  // Items for selected category
  const categoryItems = useMemo(() => {
    return unifiedItems.filter((item) => item.category === activeCategory);
  }, [unifiedItems, activeCategory]);

  // Filtered items based on search/filters
  const filteredItems = useMemo(() => {
    return categoryItems.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.assignedLevel.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesLevel = levelFilter === 'ALL' || item.assignedLevel.includes(levelFilter);
      const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;

      return matchesSearch && matchesLevel && matchesStatus;
    });
  }, [categoryItems, searchQuery, levelFilter, statusFilter]);

  // Audio Handler
  const toggleAudio = (url: string) => {
    if (playingAudioUrl === url) {
      setPlayingAudioUrl(null);
    } else {
      setPlayingAudioUrl(url);
    }
  };

  // Actions
  const handleDuplicate = (item: ContentItem) => {
    if (isAuditor) return;
    if (item.category === 'Sutras' && item.rawItem) {
      const newSutra: SutraCatalogItem = {
        ...item.rawItem,
        id: `SUT-${Date.now()}`,
        name: `${item.name} (Copy)`,
      };
      onAddSutra(newSutra);
    } else if (item.category === 'Stavans' && item.rawItem) {
      const newStavan: StavanItem = {
        ...item.rawItem,
        id: `STV-${Date.now()}`,
        title: `${item.name} (Copy)`,
      };
      onAddStavan(newStavan);
    } else if (item.category === 'Audio Library' && item.rawItem) {
      const newAudio: AudioAsset = {
        ...item.rawItem,
        id: `AUD-${Date.now()}`,
        title: `${item.name} (Copy)`,
      };
      onAddAudio(newAudio);
    } else if (item.category === 'Games' && item.rawItem) {
      const newGame: QuizGameItem = {
        ...item.rawItem,
        id: `GM-${Date.now()}`,
        title: `${item.name} (Copy)`,
      };
      onAddGame(newGame);
    } else if (item.category === 'Stories') {
      const copy: ContentItem = {
        ...item,
        id: `STORY-${Date.now()}`,
        name: `${item.name} (Copy)`,
      };
      setCustomStories((prev) => [copy, ...prev]);
    } else {
      const copy: ContentItem = {
        ...item,
        id: `FUT-${Date.now()}`,
        name: `${item.name} (Copy)`,
      };
      setCustomFuture((prev) => [copy, ...prev]);
    }
    addToast('success', 'Content Duplicated', `Created a duplicate copy of ${item.name}`);
  };

  const handleToggleArchive = (item: ContentItem) => {
    if (isAuditor) return;
    const nextStatus = item.status === 'Active' ? 'Archived' : 'Active';
    if (item.category === 'Stories') {
      setCustomStories((prev) => prev.map((s) => (s.id === item.id ? { ...s, status: nextStatus } : s)));
    } else if (item.category === 'Future Categories') {
      setCustomFuture((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: nextStatus } : f)));
    }
    addToast('info', 'Status Updated', `${item.name} status set to ${nextStatus}`);
  };

  const handleDelete = (item: ContentItem) => {
    if (isAuditor) return;
    if (item.category === 'Stavans') onDeleteStavan(item.id);
    else if (item.category === 'Audio Library') onDeleteAudio(item.id);
    else if (item.category === 'Games') onDeleteGame(item.id);
    else if (item.category === 'Stories') setCustomStories((prev) => prev.filter((s) => s.id !== item.id));
    else if (item.category === 'Future Categories') setCustomFuture((prev) => prev.filter((f) => f.id !== item.id));
    addToast('warning', 'Content Deleted', `Removed ${item.name} from ${item.category}`);
  };

  const handleAssignLevelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!levelAssignItem || isAuditor) return;

    if (levelAssignItem.category === 'Sutras' && levelAssignItem.rawItem) {
      const lvlNum = parseInt(assignLevelSelect.replace(/\D/g, '')) || 1;
      onUpdateSutra({ ...levelAssignItem.rawItem, targetLevel: lvlNum });
    } else if (levelAssignItem.category === 'Audio Library' && levelAssignItem.rawItem) {
      const lvlNum = parseInt(assignLevelSelect.replace(/\D/g, '')) || 1;
      onUpdateAudio({ ...levelAssignItem.rawItem, levelTarget: lvlNum });
    } else if (levelAssignItem.category === 'Games' && levelAssignItem.rawItem) {
      const lvlNum = parseInt(assignLevelSelect.replace(/\D/g, '')) || 1;
      onUpdateGame({ ...levelAssignItem.rawItem, levelTarget: lvlNum });
    } else if (levelAssignItem.category === 'Stories') {
      setCustomStories((prev) =>
        prev.map((s) => (s.id === levelAssignItem.id ? { ...s, assignedLevel: assignLevelSelect } : s))
      );
    }
    addToast('success', 'Level Assigned', `Updated assigned level for ${levelAssignItem.name}`);
    setLevelAssignItem(null);
  };

  // Add Item Modal Form state
  const [newContentForm, setNewContentForm] = useState({
    title: '',
    category: 'Sutras' as ContentCategory,
    assignedLevel: 'Level 1',
    type: 'General',
    description: '',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
  });

  const handleAddContentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    const category = newContentForm.category;
    if (category === 'Sutras') {
      const lvlNum = parseInt(newContentForm.assignedLevel.replace(/\D/g, '')) || 1;
      onAddSutra({
        id: `SUT-${Date.now()}`,
        name: newContentForm.title,
        targetLevel: lvlNum,
        prakrit: [newContentForm.description || 'Prakrit verses text...'],
        languages: ['Hindi', 'English'],
        audioUrl: newContentForm.audioUrl,
        pdfLyrics: true,
      });
    } else if (category === 'Stavans') {
      onAddStavan({
        id: `STV-${Date.now()}`,
        title: newContentForm.title,
        dedicatedBhagwan: newContentForm.type || 'Mahavira',
        alphabetGroup: newContentForm.title.charAt(0).toUpperCase(),
        audioUrl: newContentForm.audioUrl,
        youtubeUrl: 'https://youtube.com',
        lyrics: [newContentForm.description || 'Devotional lyrics text...'],
      });
    } else if (category === 'Audio Library') {
      const lvlNum = parseInt(newContentForm.assignedLevel.replace(/\D/g, '')) || 1;
      onAddAudio({
        id: `AUD-${Date.now()}`,
        title: newContentForm.title,
        category: 'Sutra',
        levelTarget: lvlNum,
        duration: '04:15',
        fileSize: '3.8 MB',
        audioUrl: newContentForm.audioUrl,
      });
    } else if (category === 'Games') {
      const lvlNum = parseInt(newContentForm.assignedLevel.replace(/\D/g, '')) || 1;
      onAddGame({
        id: `GM-${Date.now()}`,
        title: newContentForm.title,
        category: 'Quiz',
        levelTarget: lvlNum,
        difficulty: 'Easy',
        rewardPoints: 20,
        timesPlayed: 0,
        configJson: '{}',
      });
    } else if (category === 'Stories') {
      setCustomStories((prev) => [
        {
          id: `STORY-${Date.now()}`,
          name: newContentForm.title,
          category: 'Stories',
          assignedLevel: newContentForm.assignedLevel,
          type: newContentForm.type || 'Moral Story',
          status: 'Active',
          lastUpdated: new Date().toISOString().split('T')[0],
          rawItem: { description: newContentForm.description },
        },
        ...prev,
      ]);
    } else {
      setCustomFuture((prev) => [
        {
          id: `FUT-${Date.now()}`,
          name: newContentForm.title,
          category: 'Future Categories',
          assignedLevel: newContentForm.assignedLevel,
          type: newContentForm.type || 'Interactive Module',
          status: 'Draft',
          lastUpdated: new Date().toISOString().split('T')[0],
          rawItem: { description: newContentForm.description },
        },
        ...prev,
      ]);
    }

    addToast('success', 'Content Created', `Added ${newContentForm.title} to ${category}`);
    setIsAddModalOpen(false);
    setNewContentForm({
      title: '',
      category: activeCategory,
      assignedLevel: 'Level 1',
      type: 'General',
      description: '',
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
    });
  };

  const pendingSubmissions = submissions.filter((s) => s.status === 'Pending');

  const getContentStatusVariant = (status: ContentItem['status']) => {
    if (status === 'Active') return 'success' as const;
    if (status === 'Archived') return 'outline' as const;
    return 'warning' as const;
  };

  const getSubmissionStatusVariant = (status: GathaSubmission['status']) => {
    if (status === 'Approved') return 'success' as const;
    if (status === 'Rejected') return 'danger' as const;
    return 'warning' as const;
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Learning Content Repository</h1>
        </div>

        <div className="flex items-center gap-2">
          {activeCategory === 'Sutras' && (
            <Button
              variant={viewSubmissionsQueue ? 'secondary' : 'outline'}
              size="sm"
              leftIcon={Award}
              onClick={() => setViewSubmissionsQueue(!viewSubmissionsQueue)}
            >
              <span>Recitations Queue</span>
              {pendingSubmissions.length > 0 && (
                <Badge variant="default" className="ml-1 font-mono normal-case">
                  {pendingSubmissions.length}
                </Badge>
              )}
            </Button>
          )}

          {!isAuditor && (
            <Button
              leftIcon={Plus}
              className="shrink-0"
              onClick={() => {
                setNewContentForm((prev) => ({ ...prev, category: activeCategory }));
                setIsAddModalOpen(true);
              }}
            >
              Add Content
            </Button>
          )}
        </div>
      </div>

      {/* CATEGORY SELECTOR CARDS */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { id: 'Sutras', label: 'Sutras', icon: BookOpen, count: sutras.length },
          { id: 'Stavans', label: 'Stavans', icon: Music, count: stavans.length },
          { id: 'Audio Library', label: 'Audio Library', icon: FileAudio, count: audioAssets.length },
          { id: 'Games', label: 'Games', icon: Gamepad2, count: games.length },
          { id: 'Stories', label: 'Stories', icon: Bookmark, count: customStories.length },
          { id: 'Future Categories', label: 'Future Content', icon: Sparkles, count: customFuture.length },
        ].map((cat) => {
          const Icon = cat.icon;
          const isSelected = activeCategory === cat.id;

          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => {
                setActiveCategory(cat.id as ContentCategory);
                setViewSubmissionsQueue(false);
              }}
              className={`flex cursor-pointer flex-col justify-between space-y-2 rounded-2xl border p-3.5 text-left transition-all ${
                isSelected
                  ? 'border-[var(--gv-brand)] bg-[var(--gv-brand)] text-white shadow-sm'
                  : 'admin-card border-[var(--gv-border)] bg-white text-[var(--gv-ink)] hover:bg-[var(--gv-surface)]'
              }`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`h-5 w-5 ${isSelected ? 'text-emerald-200' : 'text-[var(--gv-brand)]'}`} />
                <span
                  className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-bold ${
                    isSelected ? 'bg-white/20 text-white' : 'bg-[var(--gv-surface)] text-[var(--gv-ink-muted)]'
                  }`}
                >
                  {cat.count}
                </span>
              </div>
              <div>
                <h3 className="text-xs font-bold leading-snug">{cat.label}</h3>
                <span className={`text-[10px] ${isSelected ? 'text-emerald-100' : 'text-[var(--gv-ink-muted)]'}`}>
                  File Management
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* STUDENT RECITATIONS SUBMISSIONS QUEUE VIEW */}
      {viewSubmissionsQueue && activeCategory === 'Sutras' ? (
        <div className="admin-card space-y-4 p-5">
          <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
            <div>
              <h2 className="text-sm font-bold text-[var(--gv-ink)]">Student Recitations Submissions Queue</h2>
              <p className="text-xs text-[var(--gv-ink-muted)]">
                Review student audio recordings and award points for Sutra Gathas.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setViewSubmissionsQueue(false)}>
              Back to Sutras Table
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-xs text-[var(--gv-ink-muted)]">
              <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 font-bold">Sub ID</th>
                  <th className="px-4 py-3 font-bold">Student</th>
                  <th className="px-4 py-3 font-bold">Sutra Name</th>
                  <th className="px-4 py-3 font-bold">Audio Recitation</th>
                  <th className="px-4 py-3 font-bold">Submission Date</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 text-right font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white font-medium">
                {submissions.map((sub) => (
                  <tr key={sub.id} className="transition-colors hover:bg-stone-50/80">
                    <td className="px-4 py-3.5 font-mono font-bold text-[var(--gv-brand)]">{sub.id}</td>
                    <td className="px-4 py-3.5 font-bold text-[var(--gv-ink)]">{sub.studentName}</td>
                    <td className="px-4 py-3.5">
                      <div className="font-bold text-[var(--gv-ink)]">{sub.sutraName}</div>
                      <span className="font-mono text-[10px] text-[var(--gv-ink-muted)]">{sub.gathaCount} Gatha(s) Recited</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <Button
                        size="sm"
                        variant={playingAudioUrl === sub.audioUrl ? 'primary' : 'outline'}
                        leftIcon={playingAudioUrl === sub.audioUrl ? Pause : Play}
                        onClick={() => toggleAudio(sub.audioUrl)}
                      >
                        {playingAudioUrl === sub.audioUrl ? 'Pause' : 'Listen Recording'}
                      </Button>
                    </td>
                    <td className="px-4 py-3.5 font-mono text-[var(--gv-ink-muted)]">{sub.submittedDate}</td>
                    <td className="px-4 py-3.5">
                      <Badge variant={getSubmissionStatusVariant(sub.status)}>{sub.status}</Badge>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {sub.status === 'Pending' && !isAuditor ? (
                        <Button
                          size="sm"
                          leftIcon={CheckCircle2}
                          onClick={() => setSelectedSubmission(sub)}
                        >
                          Review & Grade
                        </Button>
                      ) : (
                        <span className="text-[11px] italic text-[var(--gv-ink-muted)]">
                          {sub.status === 'Approved' ? `Graded (+${sub.pointsAwarded} pts)` : 'Closed'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* MAIN CONTENT TABLE VIEW */
        <div className="admin-card space-y-3 overflow-hidden">
          {/* Controls Header */}
          <div className="flex flex-col items-center justify-between gap-3 border-b border-[var(--gv-border)] bg-[var(--gv-surface)] p-4 lg:flex-row">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold uppercase text-[var(--gv-ink)]">
                {activeCategory} Catalog ({filteredItems.length})
              </span>
            </div>

            {/* Search & Filters */}
            <div className="flex w-full items-center gap-2.5 lg:w-auto">
              <Search
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={`Search ${activeCategory.toLowerCase()}...`}
                containerClassName="flex-1 lg:w-64"
              />

              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="admin-input h-9 rounded-xl border border-[var(--gv-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
              >
                <option value="ALL">All Levels</option>
                <option value="Level 1">Level 1</option>
                <option value="Level 2">Level 2</option>
                <option value="Level 3">Level 3</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-input h-9 rounded-xl border border-[var(--gv-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
              >
                <option value="ALL">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
                <option value="Draft">Draft</option>
              </select>

              {(searchQuery || levelFilter !== 'ALL' || statusFilter !== 'ALL') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setLevelFilter('ALL');
                    setStatusFilter('ALL');
                  }}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* TABLE */}
          {filteredItems.length === 0 ? (
            <div className="space-y-2 bg-[var(--gv-surface)] py-16 text-center">
              <BookOpen className="mx-auto h-8 w-8 text-[var(--gv-ink-muted)]" />
              <p className="text-sm font-bold text-[var(--gv-ink)]">No {activeCategory} found</p>
              <p className="text-xs text-[var(--gv-ink-muted)]">Try adjusting your search filter or add a new content file.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs text-[var(--gv-ink-muted)]">
                <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 font-bold">Name</th>
                    <th className="px-4 py-3 font-bold">Assigned Level</th>
                    <th className="px-4 py-3 font-bold">Type</th>
                    <th className="px-4 py-3 font-bold">Status</th>
                    <th className="px-4 py-3 font-bold">Last Updated</th>
                    <th className="px-4 py-3 text-right font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 bg-white font-medium">
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="transition-colors hover:bg-stone-50/80">
                      {/* Name */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2 text-xs font-bold text-[var(--gv-ink)]">
                          <span>{item.name}</span>
                          <span className="font-mono text-[10px] font-normal text-[var(--gv-ink-muted)]">({item.id})</span>
                        </div>
                      </td>

                      {/* Assigned Level */}
                      <td className="px-4 py-3.5">
                        <Badge variant="soft" className="normal-case">
                          {item.assignedLevel}
                        </Badge>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3.5 font-mono text-[var(--gv-ink-muted)]">{item.type}</td>

                      {/* Status */}
                      <td className="px-4 py-3.5">
                        <Badge variant={getContentStatusVariant(item.status)}>{item.status}</Badge>
                      </td>

                      {/* Last Updated */}
                      <td className="px-4 py-3.5 font-mono text-[11px] text-[var(--gv-ink-muted)]">{item.lastUpdated}</td>

                      {/* Actions */}
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* View */}
                          <Tooltip content="View Details">
                            <Button
                              variant="ghost"
                              size="icon"
                              leftIcon={Eye}
                              aria-label="View Details"
                              onClick={() => setViewingItem(item)}
                            />
                          </Tooltip>

                          {/* Assign Level */}
                          {!isAuditor && (
                            <Tooltip content="Assign Level">
                              <Button
                                variant="ghost"
                                size="icon"
                                leftIcon={Layers}
                                aria-label="Assign Level"
                                className="text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                                onClick={() => {
                                  setLevelAssignItem(item);
                                  setAssignLevelSelect(item.assignedLevel);
                                }}
                              />
                            </Tooltip>
                          )}

                          {/* Duplicate */}
                          {!isAuditor && (
                            <Tooltip content="Duplicate Content">
                              <Button
                                variant="ghost"
                                size="icon"
                                leftIcon={Copy}
                                aria-label="Duplicate Content"
                                className="text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                                onClick={() => handleDuplicate(item)}
                              />
                            </Tooltip>
                          )}

                          {/* Archive */}
                          {!isAuditor && (
                            <Tooltip content={item.status === 'Active' ? 'Archive Content' : 'Unarchive Content'}>
                              <Button
                                variant="ghost"
                                size="icon"
                                leftIcon={Archive}
                                aria-label={item.status === 'Active' ? 'Archive Content' : 'Unarchive Content'}
                                className="text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                                onClick={() => handleToggleArchive(item)}
                              />
                            </Tooltip>
                          )}

                          {/* Delete */}
                          {!isAuditor && (
                            <Tooltip content="Delete Content">
                              <Button
                                variant="ghost"
                                size="icon"
                                leftIcon={Trash2}
                                aria-label="Delete Content"
                                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                                onClick={() => handleDelete(item)}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* VIEW ITEM MODAL */}
      {viewingItem && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <div>
                <Badge variant="soft" className="font-mono uppercase">
                  {viewingItem.category}
                </Badge>
                <h3 className="mt-1 text-base font-bold text-[var(--gv-ink)]">{viewingItem.name}</h3>
              </div>
              <Button variant="ghost" size="icon" leftIcon={X} aria-label="Close" onClick={() => setViewingItem(null)} />
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Assigned Level:</span>
                  <strong className="font-bold text-[var(--gv-ink)]">{viewingItem.assignedLevel}</strong>
                </div>
                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Type / Category:</span>
                  <strong className="font-bold text-[var(--gv-ink)]">{viewingItem.type}</strong>
                </div>
                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Status:</span>
                  <strong className="font-bold text-[var(--gv-ink)]">{viewingItem.status}</strong>
                </div>
                <div>
                  <span className="block text-[10px] text-[var(--gv-ink-muted)]">Last Updated:</span>
                  <strong className="font-mono font-bold text-[var(--gv-ink)]">{viewingItem.lastUpdated}</strong>
                </div>
              </div>

              {viewingItem.rawItem?.description && (
                <div className="rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                  <span className="mb-0.5 block text-[10px] text-[var(--gv-ink-muted)]">Description:</span>
                  <p className="leading-relaxed text-[var(--gv-ink)]">{viewingItem.rawItem.description}</p>
                </div>
              )}

              {viewingItem.rawItem?.audioUrl && (
                <div className="pt-2">
                  <span className="mb-1 block text-[10px] text-[var(--gv-ink-muted)]">Audio Player:</span>
                  <Button
                    className="w-full"
                    leftIcon={playingAudioUrl === viewingItem.rawItem.audioUrl ? Pause : Play}
                    onClick={() => toggleAudio(viewingItem.rawItem.audioUrl)}
                  >
                    {playingAudioUrl === viewingItem.rawItem.audioUrl ? 'Pause Audio' : 'Play Audio Recording'}
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-end border-t border-[var(--gv-border)] pt-3">
              <Button variant="secondary" onClick={() => setViewingItem(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ASSIGN LEVEL MODAL */}
      {levelAssignItem && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-md space-y-4 p-6 text-[var(--gv-ink)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">Assign Level to Content</h3>
              <Button variant="ghost" size="icon" leftIcon={X} aria-label="Close" onClick={() => setLevelAssignItem(null)} />
            </div>

            <form onSubmit={handleAssignLevelSubmit} className="space-y-4 text-xs">
              <div>
                <span className="block text-[10px] text-[var(--gv-ink-muted)]">Content Name:</span>
                <strong className="mt-0.5 block text-sm font-bold text-[var(--gv-ink)]">{levelAssignItem.name}</strong>
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-[11px] font-bold text-[var(--gv-ink)]">Select Pathshala Level</label>
                <select
                  value={assignLevelSelect}
                  onChange={(e) => setAssignLevelSelect(e.target.value)}
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  <option value="Level 1 - Prarambhik">Level 1 - Prarambhik</option>
                  <option value="Level 2 - Madhyamik">Level 2 - Madhyamik</option>
                  <option value="Level 3 - Shravak Junior">Level 3 - Shravak Junior</option>
                  <option value="All Levels">All Levels</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 border-t border-[var(--gv-border)] pt-3">
                <Button type="button" variant="secondary" onClick={() => setLevelAssignItem(null)}>
                  Cancel
                </Button>
                <Button type="submit">Save Level</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD CONTENT MODAL */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">Add New Learning Content</h3>
              <Button variant="ghost" size="icon" leftIcon={X} aria-label="Close" onClick={() => setIsAddModalOpen(false)} />
            </div>

            <form onSubmit={handleAddContentSubmit} className="space-y-3.5 text-xs">
              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Content Category</label>
                <select
                  value={newContentForm.category}
                  onChange={(e) =>
                    setNewContentForm({ ...newContentForm, category: e.target.value as ContentCategory })
                  }
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  <option value="Sutras">Sutras</option>
                  <option value="Stavans">Stavans</option>
                  <option value="Audio Library">Audio Library</option>
                  <option value="Games">Games</option>
                  <option value="Stories">Stories</option>
                  <option value="Future Categories">Future Categories</option>
                </select>
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">
                  Title / Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newContentForm.title}
                  onChange={(e) => setNewContentForm({ ...newContentForm, title: e.target.value })}
                  placeholder="e.g., Logassa Sutra or Neminath Stavan"
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Assigned Level</label>
                  <select
                    value={newContentForm.assignedLevel}
                    onChange={(e) => setNewContentForm({ ...newContentForm, assignedLevel: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Level 1">Level 1</option>
                    <option value="Level 2">Level 2</option>
                    <option value="Level 3">Level 3</option>
                    <option value="All Levels">All Levels</option>
                  </select>
                </div>

                <Input
                  label="Type / Sub-type"
                  value={newContentForm.type}
                  onChange={(e) => setNewContentForm({ ...newContentForm, type: e.target.value })}
                  placeholder="e.g. Recitation, Moral, Hymn"
                />
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Description / Lyrics / Verses</label>
                <textarea
                  rows={3}
                  value={newContentForm.description}
                  onChange={(e) => setNewContentForm({ ...newContentForm, description: e.target.value })}
                  placeholder="Enter details, Prakrit verses, or lyrics..."
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-[var(--gv-border)] pt-3">
                <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Content</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REVIEW & GRADE SUBMISSION MODAL */}
      {selectedSubmission && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">Review Audio Recitation</h3>
              <Button variant="ghost" size="icon" leftIcon={X} aria-label="Close" onClick={() => setSelectedSubmission(null)} />
            </div>

            <div className="space-y-3 text-xs">
              <div className="space-y-1 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                <div>
                  Student: <strong className="text-[var(--gv-ink)]">{selectedSubmission.studentName}</strong>
                </div>
                <div>
                  Sutra: <strong className="text-[var(--gv-brand)]">{selectedSubmission.sutraName}</strong> ({selectedSubmission.gathaCount} Gathas)
                </div>
              </div>

              <Input
                label="Award Points"
                type="number"
                value={reviewPoints}
                onChange={(e) => setReviewPoints(parseInt(e.target.value) || 0)}
              />

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Feedback Notes</label>
                <textarea
                  rows={2}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Excellent pronunciation and rhythm..."
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-[var(--gv-border)] pt-3">
                <Button
                  type="button"
                  variant="outline"
                  className="border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                  onClick={() => {
                    onRejectSubmission(selectedSubmission, reviewNotes || 'Needs practice');
                    setSelectedSubmission(null);
                    addToast('warning', 'Submission Rejected', 'Sent feedback for re-recording.');
                  }}
                >
                  Reject & Re-record
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    onApproveSubmission(selectedSubmission, reviewPoints, reviewNotes || 'Well recited');
                    setSelectedSubmission(null);
                    addToast('success', 'Submission Approved', `Awarded ${reviewPoints} points.`);
                  }}
                >
                  Approve & Award Points
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
